import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { GET_RECENT_ORDERS } from '../graphql/queries'
import { Order, OrderStatus, OrderSource } from '../types/order'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  Bars3Icon,
} from '@heroicons/react/24/outline'

type DateRange = 'today' | '7days'
type ViewMode = 'table' | 'cards'

const STATUS_STYLES: Record<OrderStatus, string> = {
  UNPAID: 'bg-red-100 text-red-800',
  RECEIVED: 'bg-orange-100 text-orange-800',
  SCHEDULED: 'bg-cyan-100 text-cyan-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-amber-100 text-amber-800',
  READY: 'bg-green-100 text-green-800',
  IN_DELIVERY: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-purple-100 text-purple-800',
}

const STATUS_LABELS: Record<OrderStatus, string> = {
  UNPAID: 'Unpaid',
  RECEIVED: 'Needs Review',
  SCHEDULED: 'Gepland',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  IN_DELIVERY: 'In Delivery',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash',
  CARD: 'Pin',
  ONLINE: 'Online',
}

const ORDER_TYPE_LABELS: Record<string, string> = {
  DINE_IN: 'Dine in',
  TAKEAWAY: 'Takeaway',
  DELIVERY: 'Delivery',
}

// Delivery gets a badge rather than plain text: it is the one order type that changes
// how the order must be packed and handed over, so it has to survive a glance down the
// list. Every other type stays quiet.
const renderOrderType = (orderType: string) =>
  orderType === 'DELIVERY' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-teal-100 text-teal-800">
      Delivery
    </span>
  ) : (
    <span className="text-sm text-gray-600">{ORDER_TYPE_LABELS[orderType] ?? orderType}</span>
  )

function getDateFilters(range: DateRange) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (range === 'today') {
    return { date_from: today.toISOString(), date_to: now.toISOString() }
  }
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
  return { date_from: sevenDaysAgo.toISOString(), date_to: now.toISOString() }
}

function formatCents(cents: number) {
  return `€${(cents / 100).toFixed(2)}`
}

function getCustomerDisplay(order: Order) {
  if (order.customer_name) return order.customer_name
  if (order.customer_email) return order.customer_email
  if (order.customer_phone) return order.customer_phone
  return null
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  const rem = mins % 60
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`
}

function getStatusTimings(order: Order): { duration: string; label: string }[] {
  const t = order.statusTimings
  if (!t) return []
  const result: { duration: string; label: string }[] = []
  if (t.time_in_confirmed_seconds) result.push({ label: 'Confirmed', duration: formatDuration(t.time_in_confirmed_seconds) })
  if (t.time_in_preparing_seconds) result.push({ label: 'Preparing', duration: formatDuration(t.time_in_preparing_seconds) })
  if (t.time_in_ready_seconds) result.push({ label: 'Ready', duration: formatDuration(t.time_in_ready_seconds) })
  return result
}

function getPrimaryPayment(order: Order) {
  if (!order.payments || order.payments.length === 0) return null
  return order.payments.find(p => p.transaction_type === 'SALE') ?? order.payments[0]
}

const OrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const { hasAnyPermission } = usePermissions()

  const can7Days = hasAnyPermission([
    PERMISSIONS.ORDER_LIST_7DAYS,
    PERMISSIONS.ORDER_LIST_7DAYS_PLANNED,
    PERMISSIONS.ORDER_LIST_ALL,
    PERMISSIONS.ORDER_DETAILS_7DAYS,
    PERMISSIONS.ORDER_DETAILS_ALL,
  ])

  const [dateRange, setDateRange] = useState<DateRange>('today')

  // If user loses 7-day access, snap back to today
  useEffect(() => {
    if (!can7Days && dateRange === '7days') setDateRange('today')
  }, [can7Days, dateRange])
  const [sourceFilter, setSourceFilter] = useState<OrderSource | ''>('')
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('orders-view-mode') as ViewMode) ?? 'table'
  })

  const apiFilters = useMemo(() => ({
    ...getDateFilters(dateRange),
    ...(statusFilter ? { status: statusFilter } : {}),
  }), [dateRange, statusFilter])

  const { data, loading, error, refetch } = useQuery(GET_RECENT_ORDERS, {
    variables: { filters: apiFilters },
    fetchPolicy: 'cache-and-network',
  })

  const orders: Order[] = data?.orders ?? []

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (sourceFilter && order.order_source !== sourceFilter) return false
      if (!searchTerm) return true
      const lower = searchTerm.toLowerCase()
      return (
        order.order_number.toLowerCase().includes(lower) ||
        (order.customer_name?.toLowerCase().includes(lower) ?? false) ||
        (order.customer_email?.toLowerCase().includes(lower) ?? false) ||
        (order.customer_phone?.toLowerCase().includes(lower) ?? false) ||
        order.staffUser.name.toLowerCase().includes(lower)
      )
    })
  }, [orders, sourceFilter, searchTerm])

  const handleViewMode = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem('orders-view-mode', mode)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }

  // Dormant pre-orders sit in this list by created_at, so the "Gepland" chip
  // carries the fulfillment moment (date + time — it can be days away) to keep
  // them distinguishable from actionable orders.
  const formatScheduledFor = (iso: string) =>
    new Date(iso).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })

  const formatDateTime = (dateStr: string) => {
    const ms = Number(dateStr)
    const date = isNaN(ms) ? new Date(dateStr) : new Date(ms)
    if (dateRange === 'today') {
      return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to load orders</h2>
          <p className="text-gray-500 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {loading && orders.length === 0
                  ? 'Loading...'
                  : `${filteredOrders.length} order${filteredOrders.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Date range toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setDateRange('today')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  dateRange === 'today'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Today
              </button>
              {can7Days && (
                <button
                  onClick={() => setDateRange('7days')}
                  className={`px-4 py-2 text-sm font-medium border-l border-gray-200 transition-colors ${
                    dateRange === '7days'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Last 7 days
                </button>
              )}
            </div>

            {/* Source filter */}
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value as OrderSource | '')}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            >
              <option value="">All sources</option>
              <option value="POS">POS</option>
              <option value="WEBSHOP">Webshop</option>
            </select>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as OrderStatus | '')}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            >
              <option value="">All statuses</option>
              <option value="UNPAID">Unpaid</option>
              {/* Note: the page's date window filters on created_at, so a pre-order
                  placed days ago for today won't show under "Today" — the dashboard
                  "Planned orders today" widget is the canonical due-today surface. */}
              <option value="SCHEDULED">Gepland</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="PREPARING">Preparing</option>
              <option value="READY">Ready</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="REFUNDED">Refunded</option>
            </select>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order #, customer, staff..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Clear filters */}
            {(sourceFilter || statusFilter || searchTerm) && (
              <button
                onClick={() => {
                  setSourceFilter('')
                  setStatusFilter('')
                  setSearchTerm('')
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear filters
              </button>
            )}

            {/* View toggle */}
            <div className="ml-auto flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => handleViewMode('table')}
                title="Table view"
                className={`p-2 transition-colors ${
                  viewMode === 'table'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <Bars3Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleViewMode('cards')}
                title="Card view"
                className={`p-2 border-l border-gray-200 transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Empty / loading states */}
        {loading && orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="text-sm text-gray-400">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <p className="text-gray-500 font-medium">No orders found</p>
            {(searchTerm || sourceFilter || statusFilter) && (
              <p className="text-sm text-gray-400">Try adjusting your filters</p>
            )}
          </div>
        ) : viewMode === 'table' ? (

          /* ── Table view ── */
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created at</th>
                    <th className="px-2 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredOrders.map(order => {
                    const payment = getPrimaryPayment(order)
                    const customer = getCustomerDisplay(order)
                    const timings = getStatusTimings(order)
                    return (
                      <tr
                        key={order.id}
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{order.order_number}</span>
                        </td>
                        <td className="px-4 py-3 max-w-[180px]">
                          {customer
                            ? <span className="text-sm text-gray-800 truncate block" title={customer}>{customer}</span>
                            : <span className="text-sm text-gray-400 italic">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            order.order_source === 'POS' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'
                          }`}>
                            {order.order_source === 'POS' ? 'POS' : 'Webshop'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {payment ? PAYMENT_METHOD_LABELS[payment.payment_method] ?? payment.payment_method : <span className="text-gray-400 italic">—</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{formatCents(order.total_cents)}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {renderOrderType(order.order_type)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[order.status] ?? 'bg-gray-100 text-gray-700'}`}>
                              {STATUS_LABELS[order.status] ?? order.status}
                              {order.status === 'SCHEDULED' && order.scheduled_for && ` · ${formatScheduledFor(order.scheduled_for)}`}
                            </span>
                            {order.externally_handled && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                                title="Handled externally via HubRise (not in POS revenue)"
                              >
                                Ext.
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{order.staffUser.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          {timings.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {timings.map(t => (
                                <span key={t.label} className="text-xs text-gray-500">
                                  <span className="text-gray-400">{t.label}:</span> {t.duration}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-400">{formatDateTime(order.created_at)}</span>
                        </td>
                        <td className="px-2 py-3">
                          <ChevronRightIcon className="h-4 w-4 text-gray-300" />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

        ) : (

          /* ── Card view ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredOrders.map(order => {
              const payment = getPrimaryPayment(order)
              const customer = getCustomerDisplay(order)
              const timings = getStatusTimings(order)
              return (
                <div
                  key={order.id}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <span className="text-base font-bold text-gray-900">{order.order_number}</span>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[order.status] ?? 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[order.status] ?? order.status}
                        {order.status === 'SCHEDULED' && order.scheduled_for && ` · ${formatScheduledFor(order.scheduled_for)}`}
                      </span>
                      {order.externally_handled && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                          title="Handled externally via HubRise (not in POS revenue)"
                        >
                          Ext.
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Customer */}
                  <p className="text-sm text-gray-700 mb-3 truncate" title={customer ?? undefined}>
                    {customer ?? <span className="italic text-gray-400">No customer</span>}
                  </p>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-4">
                    <div>
                      <p className="text-gray-400 uppercase tracking-wide font-medium mb-0.5">Source</p>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${
                        order.order_source === 'POS' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'
                      }`}>
                        {order.order_source === 'POS' ? 'POS' : 'Webshop'}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-400 uppercase tracking-wide font-medium mb-0.5">Type</p>
                      <div className="text-gray-700">{renderOrderType(order.order_type)}</div>
                    </div>
                    <div>
                      <p className="text-gray-400 uppercase tracking-wide font-medium mb-0.5">Payment</p>
                      <p className="text-gray-700">
                        {payment ? PAYMENT_METHOD_LABELS[payment.payment_method] ?? payment.payment_method : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 uppercase tracking-wide font-medium mb-0.5">Staff</p>
                      <p className="text-gray-700 truncate">{order.staffUser.name}</p>
                    </div>
                    {timings.map(t => (
                      <div key={t.label}>
                        <p className="text-gray-400 uppercase tracking-wide font-medium mb-0.5">{t.label}</p>
                        <p className="text-gray-700 font-medium">{t.duration}</p>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <span className="text-base font-semibold text-gray-900">{formatCents(order.total_cents)}</span>
                    <span className="text-xs text-gray-400">{formatDateTime(order.created_at)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default OrdersPage
